// Galaxy-wide ship storage (design doc 16.72). Ships live in specific
// PLACES: a hull left at a Station or a player Base stays there until the
// captain physically returns for it.
//
// **Dock keys are `"<systemId>:<locationId>"`** -- the exact format the
// old Base-only `dockedAtBaseKey` already used, since a planet's Location
// id IS its planet id (`buildSystemLocations`, expeditionScreen). That's
// why generalizing to Stations needed no new key scheme: a Station is
// simply the Location id `"station"`. Old saves therefore migrate by
// renaming the field, with no value rewriting.

import type { FleetShip } from "./ui/state";

export const STATION_LOCATION_ID = "station";

export function dockKey(systemId: string, locationId: string): string {
  return `${systemId}:${locationId}`;
}

export function parseDockKey(key: string): { systemId: string; locationId: string } | null {
  const idx = key.indexOf(":");
  if (idx <= 0 || idx === key.length - 1) return null;
  return { systemId: key.slice(0, idx), locationId: key.slice(idx + 1) };
}

export function systemOfDockKey(key: string): string | null {
  return parseDockKey(key)?.systemId ?? null;
}

export function isStationDock(key: string): boolean {
  return parseDockKey(key)?.locationId === STATION_LOCATION_ID;
}

// Only OWNED hulls are storable/boardable (16.72): a hired ship is
// commanded by its own captain and never sits waiting to be flown, and
// the hull currently being flown is by definition not in storage.
export function storedOwnedShips(fleet: FleetShip[], currentShipInstanceId: string | null): FleetShip[] {
  return fleet.filter(
    (f) => f.source === "owned" && f.id !== currentShipInstanceId && !!f.dockedAtKey
  );
}

// Owned ships stored at this exact System+Location -- the ones the captain
// can physically board right now.
export function shipsStoredAt(
  fleet: FleetShip[],
  currentShipInstanceId: string | null,
  systemId: string,
  locationId: string
): FleetShip[] {
  const key = dockKey(systemId, locationId);
  return storedOwnedShips(fleet, currentShipInstanceId).filter((f) => f.dockedAtKey === key);
}

// Owned ships stored anywhere in this System (any Station or Base in it) --
// used by the Escape Pod, which arrives in a System rather than at a
// specific Location.
export function shipsStoredInSystem(
  fleet: FleetShip[],
  currentShipInstanceId: string | null,
  systemId: string
): FleetShip[] {
  return storedOwnedShips(fleet, currentShipInstanceId).filter(
    (f) => systemOfDockKey(f.dockedAtKey!) === systemId
  );
}

// Human-readable "where is it" for the Shipyard's roster. Takes a system
// name resolver rather than importing the galaxy data, so this module
// stays pure and testable.
export function describeDock(
  key: string | undefined,
  systemName: (systemId: string) => string | undefined
): string {
  if (!key) return "with you";
  const parsed = parseDockKey(key);
  if (!parsed) return "unknown";
  const name = systemName(parsed.systemId) ?? parsed.systemId;
  return parsed.locationId === STATION_LOCATION_ID ? `${name} Station` : `${name} — Base`;
}

// ---------------------------------------------------------------------
// The Escape Pod as a real travel mode (16.72)
// ---------------------------------------------------------------------
//
// The pod is what makes galaxy-wide ship storage playable: parking hulls
// around the galaxy only works if repositioning yourself between them is
// fast and risk-free.
//
// **Immunity is a hard rule, not a low probability.** A pod has no
// weapons, no shields and no meaningful hull, so being caught in one is
// unrecoverable by construction -- and a failsafe that can strand the
// captain permanently isn't a failsafe. Random attacks (16.70), ambient
// contacts (16.1) and hostile aggro all skip a podded captain entirely.
export function podIsImmune(inEscapePod: boolean): boolean {
  return inEscapePod;
}

// **50% faster than the fastest possible hull**, derived rather than
// invented (16.72):
// - in-Location: the fastest real hull is 510 m/s (Small hull's 340 m/s
//   base x the Kestrel/Scout role's 1.5, inLocationSpeed.ts) -> 765 m/s.
// - between systems: the fastest hull's combined multiplier is 1.8
//   (role 1.5 x size 1.2, 16.28) -> 2.7, i.e. a ~22.2s hop against the
//   fastest hull's ~33.3s.
export const FASTEST_HULL_IN_LOCATION_MPS = 510;
export const POD_SPEED_BONUS = 1.5;
export const POD_IN_LOCATION_MPS = FASTEST_HULL_IN_LOCATION_MPS * POD_SPEED_BONUS; // 765
export const FASTEST_HULL_SPEED_MULTIPLIER = 1.8;
export const POD_SPEED_MULTIPLIER = FASTEST_HULL_SPEED_MULTIPLIER * POD_SPEED_BONUS; // 2.7

// Hop time for the pod, using 16.28's own `60s base / multiplier` formula
// so it can never drift from how every other ship's travel is computed.
export function podHopTimeSeconds(): number {
  return 60 / POD_SPEED_MULTIPLIER;
}

// Dismissing hired escorts on a hull change (16.72). Deliberately the
// harshest of the options considered: a hired captain has no reason to sit
// idle indefinitely on an employer who just walked away, and the
// alternative (escorts quietly billing while parked and forgotten) is
// exactly the invisible credit drain the design avoids elsewhere.
// Returns the surviving fleet plus how many contracts ended, so callers
// can report it honestly rather than silently shrinking the roster.
export function dismissHiredOnHullChange(fleet: FleetShip[]): {
  fleet: FleetShip[];
  dismissed: number;
} {
  const kept = fleet.filter((f) => f.source !== "hired");
  return { fleet: kept, dismissed: fleet.length - kept.length };
}
